import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Collapse
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  Class as ClassIcon,
  Book as BookIcon,
  HowToReg as AdmissionIcon,
  AccountBalance as FinanceIcon,
  DirectionsBus as TransportIcon,
  MyLocation as TrackingIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  Badge as StaffIcon,
  Fingerprint as FingerprintIcon,
  Work as HRIcon,
  HowToReg as OnboardingIcon,
  PersonRemove as OffboardingIcon,
  VideoCall as InterviewIcon,
  BeachAccess as LeaveIcon,
  Person as PersonIcon,
  Analytics as AnalyticsIcon,
  Inventory as AssetIcon,
  MenuBook as LibraryIcon,
  MeetingRoom as FrontOfficeIcon,
  People as VisitorsIcon,
  Event as AppointmentsIcon,
  QuestionAnswer as InquiriesIcon,
  Phone as PhoneCallsIcon,
  LocalShipping as MailIcon,
  Domain as FacilityIcon,
  EventNote as BookingsIcon,
  Build as MaintenanceIcon,
  Inventory2 as InventoryIcon,
  FactCheck as InspectionsIcon,
  AccountBalance as BoardIcon,
  Verified as AffiliationIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Movie as DigitalContentIcon,
  Quiz as ExamIcon,
  Grade as GradeIcon,
  Description as ReportCardIcon,
  TrendingUp as PromotionIcon,
  SmartToy as AIIcon,
  Gavel as ComplianceIcon,
  Extension as AdvancedFeatureIcon,
  FamilyRestroom as ParentPortalIcon,
  Person as StudentPortalIcon,
  SupportAgent as TeacherPortalIcon,
  School as ScholarshipIcon,
  EventAvailable as ExamScheduleIcon,
  Grading as GradingScaleIcon,
  MenuBook as ServiceBookIcon,
  Payments as RteReimbursementIcon,
  Payments as PaymentsIcon,
  HowToReg as RegistrationIcon,
  ListAlt as ElectiveIcon,
  Category as SubjectGroupIcon,
  CloudDownload as UdiseIcon,
  Home as HostelIcon,
  Restaurant as CafeteriaIcon,
  Group as GroupIcon,
  Badge as SisIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { getRuntimeConfig } from '../../utils/runtimeConfig';
import { isModuleVisible } from '../../utils/roleUtils';
import ErrorBoundary from '../common/ErrorBoundary';

const drawerWidth = 240;

// All Indian state and UT board standards (RTE/UDISE applicable)
const INDIAN_STATE_BOARDS = [
  'ANDHRA_PRADESH','ARUNACHAL_PRADESH','ASSAM','BIHAR','CHHATTISGARH',
  'GOA','GUJARAT','HARYANA','HIMACHAL_PRADESH','JHARKHAND',
  'KARNATAKA','KERALA','MADHYA_PRADESH','MAHARASHTRA','MANIPUR',
  'MEGHALAYA','MIZORAM','NAGALAND','ODISHA','PUNJAB',
  'RAJASTHAN','SIKKIM','TAMIL_NADU','TELANGANA','TRIPURA',
  'UTTAR_PRADESH','UTTARAKHAND','WEST_BENGAL',
  'ANDAMAN_NICOBAR','CHANDIGARH','DADRA_NAGAR_HAVELI','DELHI',
  'JAMMU_KASHMIR','LADAKH','LAKSHADWEEP','PUDUCHERRY'
];

// All supported standards
const ALL_STANDARDS = ['CBSE','ICSE','IB','CAMBRIDGE','OTHER',...INDIAN_STATE_BOARDS];

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Management Dashboard', icon: <AnalyticsIcon />, path: '/management-dashboard' },
  { text: 'School Master', icon: <SchoolIcon />, path: '/school-setup', adminOnly: true },
  { text: 'User Management', icon: <PeopleIcon />, path: '/user-management', adminOnly: true },
  { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
  { text: 'Admission', icon: <AdmissionIcon />, path: '/admission-module' },
  {
    text: 'Finance',
    icon: <FinanceIcon />,
    path: '/finance',
    submenu: [
      { text: 'Dashboard', icon: <FinanceIcon />, path: '/finance' },
      { text: 'Chart of Accounts', icon: <FinanceIcon />, path: '/finance/chart-of-accounts' },
      { text: 'General Ledger', icon: <FinanceIcon />, path: '/finance/general-ledger' },
      { text: 'Receivables (AR)', icon: <FinanceIcon />, path: '/finance/receivables' },
      { text: 'Payables (AP)', icon: <FinanceIcon />, path: '/finance/payables' },
      { text: 'Cash & Bank', icon: <FinanceIcon />, path: '/finance/cash-bank' },
      { text: 'Budgets', icon: <FinanceIcon />, path: '/finance/budgets' },
      { text: 'Expenses', icon: <FinanceIcon />, path: '/finance/expenses' },
      { text: 'Income', icon: <FinanceIcon />, path: '/finance/income' },
      { text: 'Statements', icon: <FinanceIcon />, path: '/finance/statements' },
      { text: 'Reports', icon: <FinanceIcon />, path: '/finance/reports' },
      { text: 'Approvals', icon: <FinanceIcon />, path: '/finance/approvals' },
      { text: 'Tax & Compliance', icon: <FinanceIcon />, path: '/finance/tax' },
      { text: 'Audit Logs', icon: <FinanceIcon />, path: '/finance/audit' },
      { text: 'AI Insights', icon: <FinanceIcon />, path: '/finance/ai' },
      { text: 'Integrations', icon: <FinanceIcon />, path: '/finance/integrations' },
      { text: 'Procurement', icon: <FinanceIcon />, path: '/finance/procurement' },
      { text: 'Fixed Assets', icon: <FinanceIcon />, path: '/finance/fixed-assets' },
    ]
  },
  { text: 'Fees', icon: <FinanceIcon />, path: '/fees' },
  {
    text: 'SIS',
    icon: <SisIcon />,
    path: '/students',
    submenu: [
      { text: 'Student Directory', icon: <PeopleIcon />, path: '/students' },
      { text: 'Admissions', icon: <RegistrationIcon />, path: '/admissions' },
      { text: 'Student Analytics', icon: <AnalyticsIcon />, path: '/students/analytics' },
      { text: 'Promotions', icon: <PromotionIcon />, path: '/academic-module' },
      { text: 'Alumni', icon: <SchoolIcon />, path: '/students/alumni' },
    ]
  },
  {
    text: 'Attendance',
    icon: <AssignmentIcon />,
    path: '/attendance-module',
    submenu: [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/attendance-module' },
      { text: 'Mark Attendance', icon: <AssignmentIcon />, path: '/attendance-module/mark' },
      { text: 'Sessions', icon: <AssignmentIcon />, path: '/attendance-module/sessions' },
      { text: 'Leave Management', icon: <AssignmentIcon />, path: '/attendance-module/leaves' },
      { text: 'Reports', icon: <AnalyticsIcon />, path: '/attendance-module/reports' },
      { text: 'Method Config', icon: <AssignmentIcon />, path: '/attendance-module/method-config' },
      { text: 'Audit Logs', icon: <AssignmentIcon />, path: '/attendance-module/audit-logs' },
    ]
  },
  {
    text: 'Examination',
    icon: <ExamIcon />,
    path: '/examination-module',
  },
  {
    text: 'Academic',
    icon: <SchoolIcon />,
    path: '/academic-module',
    submenu: [
      { text: 'Academic Management', icon: <SchoolIcon />, path: '/academic-module' },
      { text: 'Grades', icon: <SchoolIcon />, path: '/grades' },
      { text: 'Classes', icon: <ClassIcon />, path: '/classes' },
      { text: 'Courses', icon: <BookIcon />, path: '/courses' },
      { text: 'ICSE Assessments', icon: <ExamIcon />, path: '/exam-icse' },
    ]
  },
  {
    text: 'Transport',
    icon: <TransportIcon />,
    path: '/transport',
    submenu: [
      { text: 'Management', icon: <TransportIcon />, path: '/transport' },
      { text: 'Live Tracking', icon: <TrackingIcon />, path: '/transport/tracking' },
      { text: 'Reports', icon: <TransportIcon />, path: '/transport/reports' },
      { text: 'Tracking Settings', icon: <SettingsIcon />, path: '/transport/settings' }
    ]
  },
  {
    text: 'Asset Management',
    icon: <AssetIcon />,
    path: '/assets',
    submenu: [
      { text: 'Dashboard', icon: <AssetIcon />, path: '/assets' },
      { text: 'All Assets', icon: <AssetIcon />, path: '/assets/list' },
      { text: 'Staff Requests', icon: <AssetIcon />, path: '/assets/requests' },
      { text: 'Categories', icon: <AssetIcon />, path: '/assets/categories' },
      { text: 'Maintenance', icon: <AssetIcon />, path: '/assets/maintenance' },
      { text: 'Transfers', icon: <AssetIcon />, path: '/assets/transfers' },
      { text: 'Disposals', icon: <AssetIcon />, path: '/assets/disposals' }
    ]
  },
  {
    text: 'Hostel Management',
    icon: <HostelIcon />,
    path: '/hostel'
  },
  {
    text: 'Cafeteria',
    icon: <CafeteriaIcon />,
    path: '/cafeteria'
  },
  {
    text: 'Houses & Clubs',
    icon: <GroupIcon />,
    path: '/houses-clubs'
  },
  {
    text: 'Communication Center',
    icon: <NotificationsIcon />,
    path: '/communication-center'
  },
  {
    text: 'Inventory & Store',
    icon: <InventoryIcon />,
    path: '/inventory'
  },
  {
    text: 'Reports',
    icon: <AnalyticsIcon />,
    path: '/reports'
  },
  {
    text: 'Library Management',
    icon: <LibraryIcon />,
    path: '/library',
    submenu: [
      { text: 'Dashboard', icon: <LibraryIcon />, path: '/library' },
      { text: 'Books', icon: <LibraryIcon />, path: '/library/books' }
    ]
  },
  {
    text: 'Front Office',
    icon: <FrontOfficeIcon />,
    path: '/front-office',
    submenu: [
      { text: 'Dashboard', icon: <FrontOfficeIcon />, path: '/front-office' },
      { text: 'Visitors', icon: <VisitorsIcon />, path: '/front-office/visitors' },
      { text: 'Appointments', icon: <AppointmentsIcon />, path: '/front-office/appointments' },
      { text: 'Inquiries', icon: <InquiriesIcon />, path: '/front-office/inquiries' },
      { text: 'Phone Calls', icon: <PhoneCallsIcon />, path: '/front-office/phone-calls' },
      { text: 'Mail & Packages', icon: <MailIcon />, path: '/front-office/mail-packages' }
    ]
  },
  {
    text: 'Facility Management',
    icon: <FacilityIcon />,
    path: '/facility',
    submenu: [
      { text: 'Dashboard', icon: <FacilityIcon />, path: '/facility' },
      { text: 'Facilities', icon: <FacilityIcon />, path: '/facility/facilities' },
      { text: 'Bookings', icon: <BookingsIcon />, path: '/facility/bookings' },
      { text: 'Maintenance', icon: <MaintenanceIcon />, path: '/facility/maintenance' },
      { text: 'Inventory', icon: <InventoryIcon />, path: '/facility/inventory' },
      { text: 'Inspections', icon: <InspectionsIcon />, path: '/facility/inspections' }
    ]
  },
  {
    text: 'Staff',
    icon: <StaffIcon />,
    path: '/staff',
    submenu: [
      { text: 'Staff Management', icon: <StaffIcon />, path: '/staff' },
      { text: 'Biometric Enrollment', icon: <FingerprintIcon />, path: '/staff/biometric' }
    ]
  },
  {
    text: 'HR',
    icon: <HRIcon />,
    path: '/hr',
    submenu: [
      { text: 'HR Dashboard', icon: <HRIcon />, path: '/hr' },
      { text: 'HR & Payroll', icon: <HRIcon />, path: '/hr/module' },
      { text: 'Onboarding', icon: <OnboardingIcon />, path: '/hr/onboarding' },
      { text: 'Offboarding', icon: <OffboardingIcon />, path: '/hr/offboarding' },
      { text: 'Interviews', icon: <InterviewIcon />, path: '/hr/interviews' },
      { text: 'Leave Management', icon: <LeaveIcon />, path: '/hr/leaves' },
      { text: 'Self-Service Portal', icon: <PersonIcon />, path: '/hr/self-service' },
      { text: 'Payroll', icon: <PaymentsIcon />, path: '/payroll' },
      { text: 'Analytics', icon: <AnalyticsIcon />, path: '/hr/analytics' }
    ]
  },
  {
    text: 'Boards & Affiliations',
    icon: <BoardIcon />,
    path: '/boards',
    submenu: [
      { text: 'Boards', icon: <BoardIcon />, path: '/boards', standards: ALL_STANDARDS },
      { text: 'Affiliations', icon: <AffiliationIcon />, path: '/affiliations', standards: ALL_STANDARDS },
      { text: 'Scholarships', icon: <ScholarshipIcon />, path: '/boards/scholarships', standards: ALL_STANDARDS },
      { text: 'Exam Schedules', icon: <ExamScheduleIcon />, path: '/boards/exam-schedules', standards: ALL_STANDARDS },
      { text: 'Grading Scales', icon: <GradingScaleIcon />, path: '/boards/grading-scales', standards: ALL_STANDARDS },
      { text: 'Service Books', icon: <ServiceBookIcon />, path: '/boards/service-books', standards: ALL_STANDARDS },
      { text: 'RTE Reimbursements', icon: <RteReimbursementIcon />, path: '/boards/rte-reimbursements', standards: ALL_STANDARDS },
      { text: 'Board Exam Reg.', icon: <RegistrationIcon />, path: '/boards/board-exam-registrations', standards: ALL_STANDARDS },
      { text: 'Elective Choices', icon: <ElectiveIcon />, path: '/boards/elective-choices', standards: ALL_STANDARDS },
      { text: 'Subject Groups', icon: <SubjectGroupIcon />, path: '/boards/subject-groups', standards: ALL_STANDARDS },
      { text: 'UDISE Exports', icon: <UdiseIcon />, path: '/boards/udise-exports', standards: ALL_STANDARDS },
    ]
  },
  {
    text: 'Administration',
    icon: <SettingsIcon />,
    path: '/settings',
    adminOnly: true,
    submenu: [
      { text: 'Users', icon: <PeopleIcon />, path: '/users' },
      { text: 'User Roles', icon: <PeopleIcon />, path: '/user-roles' },
      { text: 'Branches', icon: <BoardIcon />, path: '/branches' },
      { text: 'School Profile', icon: <SettingsIcon />, path: '/school-profile' },
      { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
      { text: 'Audit Logs', icon: <SettingsIcon />, path: '/audit-logs' },
      { text: 'System Health', icon: <SettingsIcon />, path: '/system-health' }
    ]
  }
];

// Map menu items to modules for RBAC filtering
const getMenuModule = (item) => {
  const moduleMap = {
    'Dashboard': 'dashboard',
    'Admissions': 'admissions',
    'Academic': 'students',  // Academic is a composite — always visible; sub-items filtered individually
    'SIS': 'students',
    'Finance': 'finance',
    'Transport': 'transport',
    'Asset Management': 'assets',
    'Hostel Management': 'hostel',
    'Cafeteria': 'cafeteria',
    'Houses & Clubs': 'houses_clubs',
    'Communication Center': 'students',
    'Inventory & Store': 'assets',
    'Reports': 'students',
    'Management Dashboard': 'dashboard',
    'Library Management': 'library',
    'Front Office': 'frontoffice',
    'Facility Management': 'facility',
    'Staff': 'staff',
    'HR': 'hr',
    'Boards & Affiliations': 'boards',
    'Administration': 'admin',
  };
  return moduleMap[item.text] || null;
};

// Map submenu items to modules for RBAC filtering
const getSubmenuModule = (subItem) => {
  const text = subItem.text;
  if (text === 'Students' || text === 'Student Directory') return 'students';
  if (text === 'Student Analytics') return 'students';
  if (text === 'Admissions') return 'admissions';
  if (text === 'Promotions') return 'students';
  if (text === 'Attendance' || text === 'Dashboard' || text === 'Mark Attendance' || text === 'Sessions' || text === 'Reports') return 'attendance';
  if (text === 'Grades') return 'grades';
  if (text === 'Classes') return 'classes';
  if (text === 'Courses') return 'courses';
  if (text === 'Payroll') return 'hr';
  if (text === 'ICSE Assessments') return 'students';
  if (text === 'Lesson Plans') return 'students';
  return null;
};

const MainLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [transportOpen, setTransportOpen] = useState(false);
  const [academicOpen, setAcademicOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [hrOpen, setHrOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [frontOfficeOpen, setFrontOfficeOpen] = useState(false);
  const [facilityOpen, setFacilityOpen] = useState(false);
  const [boardsOpen, setBoardsOpen] = useState(false);
  const [assetOpen, setAssetOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [sisOpen, setSisOpen] = useState(false);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [activeStandard, setActiveStandard] = useState('CBSE');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const userRoles = user?.roles || [];

  // Filter menu items based on RBAC module visibility
  const visibleMenuItems = menuItems.filter(item => {
    // Admin-only items (Users, Branches, Settings) — visible only to admin/super_admin
    if (item.adminOnly) {
      const legacyRole = user?.role;
      return legacyRole === 'admin' || legacyRole === 'super_admin' ||
        userRoles.includes('admin') || userRoles.includes('super_admin');
    }
    const moduleName = getMenuModule(item);
    // Dashboard is always visible to authenticated users
    if (moduleName === 'dashboard') return true;
    if (!moduleName) return true;
    // Academic is a composite module — always visible; sub-items are filtered individually
    if (item.text === 'Academic') return true;
    // HR is also visible to hr_manager
    if (item.text === 'HR' && userRoles.includes('hr_manager')) return true;
    return isModuleVisible(user, moduleName);
  });

  // Filter Academic sub-items based on RBAC module visibility
  const getVisibleSubmenuItems = (item) => {
    if (!item.submenu) return item.submenu;
    return item.submenu.filter(subItem => {
      const subModule = getSubmenuModule(subItem);
      if (!subModule) return true;
      return isModuleVisible(user, subModule);
    });
  };

  useEffect(() => {
    getRuntimeConfig().then(cfg => {
      if (cfg?.activeStandard) setActiveStandard(cfg.activeStandard);
    }).catch(() => {});
  }, []);

  // Auto-open the Academic submenu when on one of its child routes
  useEffect(() => {
    const academicPaths = ['/grades', '/classes', '/courses', '/lms', '/academic-module', '/exam-icse'];
    if (academicPaths.some(p => location.pathname.startsWith(p))) {
      setAcademicOpen(true);
    }
    // Auto-open Attendance submenu when on an attendance route
    if (location.pathname.startsWith('/attendance-module')) {
      setAttendanceOpen(true);
    }
    // Auto-open SIS submenu when on a student route
    if (location.pathname.startsWith('/students')) {
      setSisOpen(true);
    }
    // Auto-open Administration submenu when on an admin route
    if (['/users', '/user-roles', '/branches', '/school-profile', '/settings', '/audit-logs', '/system-health'].some(p => location.pathname.startsWith(p))) {
      setAdminOpen(true);
    }
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
    handleMenuClose();
  };

  const handleTransportClick = () => {
    setTransportOpen(!transportOpen);
  };

  const handleAcademicClick = () => {
    setAcademicOpen(!academicOpen);
  };

  const handleStaffClick = () => {
    setStaffOpen(!staffOpen);
  };

  const handleHrClick = () => {
    setHrOpen(!hrOpen);
  };

  const handleFinanceClick = () => {
    setFinanceOpen(!financeOpen);
  };

  const handleFrontOfficeClick = () => {
    setFrontOfficeOpen(!frontOfficeOpen);
  };

  const handleFacilityClick = () => {
    setFacilityOpen(!facilityOpen);
  };

  const handleBoardsClick = () => {
    setBoardsOpen(!boardsOpen);
  };

  const handleAssetClick = () => {
    setAssetOpen(!assetOpen);
  };

  const handleLibraryClick = () => {
    setLibraryOpen(!libraryOpen);
  };

  const handleSisClick = () => {
    setSisOpen(!sisOpen);
  };

  const handleAttendanceClick = () => {
    setAttendanceOpen(!attendanceOpen);
  };

  const handleAdminClick = () => {
    setAdminOpen(!adminOpen);
  };

  const drawer = (
    <div>
      <Toolbar sx={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
        color: '#fff',
        minHeight: '64px !important',
        justifyContent: 'center',
        boxShadow: '0 4px 20px rgba(99, 102, 241, 0.25)',
      }}>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700, letterSpacing: '0.03em', color: '#fff' }}>
          SMS
        </Typography>
      </Toolbar>
      <List sx={{ pt: 1 }}>
        {visibleMenuItems.map((item) => {
          let isOpen = false;
          let handleClick = () => {};

          if (item.text === 'Transport') {
            isOpen = transportOpen;
            handleClick = handleTransportClick;
          } else if (item.text === 'Academic') {
            isOpen = academicOpen;
            handleClick = handleAcademicClick;
          } else if (item.text === 'Staff') {
            isOpen = staffOpen;
            handleClick = handleStaffClick;
          } else if (item.text === 'HR') {
            isOpen = hrOpen;
            handleClick = handleHrClick;
          } else if (item.text === 'Finance') {
            isOpen = financeOpen;
            handleClick = handleFinanceClick;
          } else if (item.text === 'Front Office') {
            isOpen = frontOfficeOpen;
            handleClick = handleFrontOfficeClick;
          } else if (item.text === 'Facility Management') {
            isOpen = facilityOpen;
            handleClick = handleFacilityClick;
          } else if (item.text === 'Boards & Affiliations') {
            isOpen = boardsOpen;
            handleClick = handleBoardsClick;
          } else if (item.text === 'Asset Management') {
            isOpen = assetOpen;
            handleClick = handleAssetClick;
          } else if (item.text === 'Library Management') {
            isOpen = libraryOpen;
            handleClick = handleLibraryClick;
          } else if (item.text === 'SIS') {
            isOpen = sisOpen;
            handleClick = handleSisClick;
          } else if (item.text === 'Attendance') {
            isOpen = attendanceOpen;
            handleClick = handleAttendanceClick;
          } else if (item.text === 'Administration') {
            isOpen = adminOpen;
            handleClick = handleAdminClick;
          }

          // Determine if this parent menu item should be highlighted
          const isParentActive = item.text === 'Academic'
            ? ['/grades', '/classes', '/courses', '/lms', '/academic-module', '/exam-icse'].some(p => location.pathname.startsWith(p))
            : item.text === 'Attendance'
            ? location.pathname.startsWith('/attendance-module')
            : item.text === 'SIS'
            ? location.pathname.startsWith('/students') || location.pathname.startsWith('/admissions')
            : location.pathname.startsWith(item.path);

          return item.submenu ? (
            <React.Fragment key={item.text}>
              <ListItem disablePadding sx={{ mx: 1, mb: 0.5, borderRadius: 2, overflow: 'hidden' }}>
                <ListItemButton
                  selected={isParentActive}
                  onClick={handleClick}
                  sx={{
                    borderRadius: 2,
                    '&.Mui-selected': {
                      background: 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(236,72,153,0.08) 100%)',
                      color: '#6366f1',
                      '& .MuiListItemIcon-root': { color: '#6366f1' },
                    },
                    '&:hover': { backgroundColor: 'rgba(99,102,241,0.06)' },
                  }}
                >
                  <ListItemIcon sx={{ color: isParentActive ? '#6366f1' : '#64748b', minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isParentActive ? 600 : 500, fontSize: '0.9rem' }} />
                  {isOpen ? <ExpandLess sx={{ color: '#6366f1' }} /> : <ExpandMore sx={{ color: '#94a3b8' }} />}
                </ListItemButton>
              </ListItem>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {getVisibleSubmenuItems(item)
                    .filter(subItem => !subItem.standards || subItem.standards.includes(activeStandard))
                    .map((subItem) => (
                      <ListItem key={subItem.text} disablePadding sx={{ mx: 1, mb: 0.3, borderRadius: 2 }}>
                        <ListItemButton
                          sx={{
                            pl: 4,
                            borderRadius: 2,
                            '&.Mui-selected': {
                              background: 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(236,72,153,0.08) 100%)',
                              color: '#6366f1',
                              '& .MuiListItemIcon-root': { color: '#6366f1' },
                            },
                            '&:hover': { backgroundColor: 'rgba(99,102,241,0.06)' },
                          }}
                          selected={location.pathname === subItem.path}
                          onClick={() => navigate(subItem.path)}
                        >
                          <ListItemIcon sx={{ color: location.pathname === subItem.path ? '#6366f1' : '#94a3b8', minWidth: 36 }}>{subItem.icon}</ListItemIcon>
                          <ListItemText primary={subItem.text} primaryTypographyProps={{ fontWeight: location.pathname === subItem.path ? 600 : 400, fontSize: '0.85rem' }} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                </List>
              </Collapse>
            </React.Fragment>
          ) : (
            <ListItem key={item.text} disablePadding sx={{ mx: 1, mb: 0.5, borderRadius: 2, overflow: 'hidden' }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2,
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(236,72,153,0.08) 100%)',
                    color: '#6366f1',
                    '& .MuiListItemIcon-root': { color: '#6366f1' },
                  },
                  '&:hover': { backgroundColor: 'rgba(99,102,241,0.06)' },
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === item.path ? '#6366f1' : '#64748b', minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: location.pathname === item.path ? 600 : 500, fontSize: '0.9rem' }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
          boxShadow: '0 4px 20px rgba(99, 102, 241, 0.25)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: '0.02em' }}>
            School Management System
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'rgba(255,255,255,0.9)' }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <IconButton onClick={handleMenuOpen} size="small" sx={{ p: 0.5 }}>
              <Avatar
                alt={user?.firstName}
                src={user?.profileImage}
                sx={{
                  width: 36,
                  height: 36,
                  background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                  fontWeight: 600,
                  border: '2px solid rgba(255,255,255,0.3)',
                }}
              >
                {user?.firstName?.[0]}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  borderRadius: 3,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                  minWidth: 160,
                }
              }}
            >
              <MenuItem onClick={handleLogout} sx={{ borderRadius: 2, mx: 1, my: 0.5 }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" sx={{ color: '#ef4444' }} />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: 'none' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none',
              boxShadow: '4px 0 24px rgba(0,0,0,0.06)',
              background: '#ffffff',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </Box>
    </Box>
  );
};

export default MainLayout;